import type { ApiHttpClient, RequestOptions } from "../interfaces/api-http-client";
import type {
  MediaInsightResponse,
  MediaStatusResult,
  TextInsightResponse,
} from "./types";

/**
 * One speaker relabel/merge instruction for `PUT /media/speakers/:mediaId`.
 * `id` is the speaker's current label (what the transcript stores), `name`
 * is the label to apply. `mergeIntoId` opts into collapsing onto an existing
 * name; `segmentIds` limits the rename to specific transcript segments.
 */
export interface SpeakerUpdateEntry {
  id: string;
  name: string;
  mergeIntoId?: string;
  segmentIds?: number[];
}

export function createMediaInsightApi(http: ApiHttpClient) {
  return {
    /**
     * `GET /media/status/:mediaId`. Generic over the response shape since
     * callers read different slices of the same endpoint (folder resolution
     * vs. live processing-status polling) — defaults to `MediaStatusResult`.
     */
    getMediaStatus<T = MediaStatusResult>(mediaId: string, options?: RequestOptions) {
      return http.get<T>(`/media/status/${mediaId}`, undefined, {
        trackingCategory: "media",
        ...options,
      });
    },

    getMediaInsight(mediaId: string) {
      return http.get<MediaInsightResponse>(`/media/insight/${mediaId}`);
    },

    /** `GET /text/insight/:mediaId` — text-note counterpart of getMediaInsight. */
    getTextInsight(mediaId: string) {
      return http.get<TextInsightResponse>(`/text/insight/${mediaId}`);
    },

    /** `PUT /media/speakers/:mediaId` — relabel, merge, or reset speakers. */
    updateSpeakers(mediaId: string, updates: SpeakerUpdateEntry[]) {
      return http.put(`/media/speakers/${mediaId}`, updates);
    },

    /** `GET /media/analyze/:mediaId` — triggers transcription + analysis. */
    analyzeMedia(mediaId: string) {
      return http.get(`/media/analyze/${mediaId}`);
    },
  };
}
